// useProgressiveFit.js
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * Öncelik sırası ile sığdırma:
 * 1. Gap'i azalt (12pt → 5pt)
 * 2. Fabric font size'ı azalt
 * 3. Description font size'ı azalt
 */
export function useProgressiveFit({
  maxHeight,
  minGap = 5/12, // 12pt'den 5pt'ye kadar (0.417)
  minFabricScale = 0.3,
  minDescriptionScale = 0.3,
  precision = 0.001,
  observeResize = false, // ResizeObserver'ı varsayılan olarak kapalı
}) {
  const ref = useRef(null);
  const [gap, setGap] = useState(1);
  const [fabricScale, setFabricScale] = useState(1);
  const [descriptionScale, setDescriptionScale] = useState(1);
  const isComputingRef = useRef(false);
  const lastValuesRef = useRef({ gap: 1, fabricScale: 1, descriptionScale: 1 });
  const timeoutRef = useRef(null);
  const rafRef = useRef(null);

  const fitsAt = (el, g, fs, ds) => {
    el.style.setProperty("--fit-gap", String(g));
    el.style.setProperty("--fit-fabric-scale", String(fs));
    el.style.setProperty("--fit-description-scale", String(ds));
    return el.scrollHeight <= maxHeight;
  };

  const binSearch = (lo, hi, test) => {
    const maxIterations = 100; // Maksimum iterasyon sayısı - sonsuz döngüyü önler
    let iterations = 0;
    
    while (hi - lo > precision && iterations < maxIterations) {
      iterations++;
      const mid = (lo + hi) / 2;
      if (test(mid)) {
        lo = mid; // sığıyor: daha büyüğünü dene
      } else {
        hi = mid; // sığmıyor: küçült
      }
    }
    return Number(lo.toFixed(3));
  };

  const measureAndFit = useCallback(() => {
    const el = ref.current;
    if (!el || isComputingRef.current) return; // Zaten hesaplama yapılıyorsa çık
    
    // Önceki timeout ve raf'ı iptal et
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    
    isComputingRef.current = true;

    // reset
    el.style.setProperty("--fit-gap", "1");
    el.style.setProperty("--fit-fabric-scale", "1");
    el.style.setProperty("--fit-description-scale", "1");

    // DOM'un güncellenmesi için requestAnimationFrame kullan (daha stabil)
    rafRef.current = requestAnimationFrame(() => {
      // Bir sonraki frame'de çalıştır (layout'un tamamlanması için)
      timeoutRef.current = setTimeout(() => {
        const currentEl = ref.current;
        if (!currentEl || !isComputingRef.current) {
          isComputingRef.current = false;
          timeoutRef.current = null;
          rafRef.current = null;
          return;
        }

        let newGap = 1;
        let newFabricScale = 1;
        let newDescriptionScale = 1;

        // 1) zaten sığıyor mu?
        if (fitsAt(currentEl, 1, 1, 1)) {
          newGap = 1;
          newFabricScale = 1;
          newDescriptionScale = 1;
        }
        // 2) Önce gap'i azalt (12pt → 5pt)
        else if (fitsAt(currentEl, minGap, 1, 1)) {
          newGap = binSearch(minGap, 1, (g) => fitsAt(currentEl, g, 1, 1));
          newFabricScale = 1;
          newDescriptionScale = 1;
          currentEl.style.setProperty("--fit-gap", String(newGap));
        }
        // 3) Gap minGap'e sabitle, fabric scale'i azalt
        else {
          currentEl.style.setProperty("--fit-gap", String(minGap));
          newGap = minGap;

          if (fitsAt(currentEl, minGap, minFabricScale, 1)) {
            newFabricScale = binSearch(minFabricScale, 1, (fs) => fitsAt(currentEl, minGap, fs, 1));
            newDescriptionScale = 1;
            currentEl.style.setProperty("--fit-fabric-scale", String(newFabricScale));
          }
          // 4) Fabric scale minFabricScale'e sabitle, description scale'i azalt
          else {
            currentEl.style.setProperty("--fit-fabric-scale", String(minFabricScale));
            newFabricScale = minFabricScale;

            if (fitsAt(currentEl, minGap, minFabricScale, minDescriptionScale)) {
              const bestDescriptionScale = binSearch(minDescriptionScale, 1, (ds) => fitsAt(currentEl, minGap, minFabricScale, ds));
              newDescriptionScale = Math.min(1, bestDescriptionScale);
              currentEl.style.setProperty("--fit-description-scale", String(newDescriptionScale));
            }
            // 5) Hepsi minimum değerlere sabitle
            else {
              newDescriptionScale = minDescriptionScale;
              currentEl.style.setProperty("--fit-description-scale", String(minDescriptionScale));
            }
          }
        }

        // Sadece değerler gerçekten değiştiyse state'i güncelle (batch update)
        const lastValues = lastValuesRef.current;
        const shouldUpdateGap = Math.abs(newGap - lastValues.gap) > 0.001;
        const shouldUpdateFabric = Math.abs(newFabricScale - lastValues.fabricScale) > 0.001;
        const shouldUpdateDescription = Math.abs(newDescriptionScale - lastValues.descriptionScale) > 0.001;

        if (shouldUpdateGap || shouldUpdateFabric || shouldUpdateDescription) {
          // Batch state updates
          if (shouldUpdateGap) {
            lastValues.gap = newGap;
            setGap(newGap);
          }
          if (shouldUpdateFabric) {
            lastValues.fabricScale = newFabricScale;
            setFabricScale(newFabricScale);
          }
          if (shouldUpdateDescription) {
            lastValues.descriptionScale = newDescriptionScale;
            setDescriptionScale(newDescriptionScale);
          }
        }

        isComputingRef.current = false;
        timeoutRef.current = null;
        rafRef.current = null;
      }, 10);
    });
  }, [maxHeight, minGap, minFabricScale, minDescriptionScale, precision]);

  const measureAndFitRef = useRef(measureAndFit);
  measureAndFitRef.current = measureAndFit;
  
  const hasInitializedRef = useRef(false);
  useLayoutEffect(() => {
    // Sadece ilk render'da çalış, sonra sadece recompute ile tetiklenir
    if (!hasInitializedRef.current && ref.current) {
      hasInitializedRef.current = true;
      // İlk render'da DOM hazır olana kadar bekle
      setTimeout(() => {
        measureAndFitRef.current();
      }, 50);
    }
  }, []); // Boş dependency array - sadece mount'ta çalışır

  useEffect(() => {
    if (!observeResize || !ref.current) return;
    
    let timeoutId;
    let lastHeight = 0;
    const ro = new ResizeObserver(() => {
      const currentEl = ref.current;
      if (!currentEl) return;
      
      // Sadece yükseklik gerçekten değiştiyse tetikle
      const currentHeight = currentEl.scrollHeight;
      if (Math.abs(currentHeight - lastHeight) < 1) return; // 1px'den küçük değişiklikleri ignore et
      lastHeight = currentHeight;
      
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (!isComputingRef.current) {
          measureAndFit();
        }
      }, 100); // Debounce süresini artırdık
    });
    
    ro.observe(ref.current);
    return () => {
      clearTimeout(timeoutId);
      ro.disconnect();
    };
  }, [observeResize, measureAndFit]);

  // Cleanup: component unmount olduğunda veya hook yeniden çalıştığında
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      isComputingRef.current = false;
    };
  }, []);

  return { ref, gap, fabricScale, descriptionScale, recompute: measureAndFit };
}

